import React from 'react'
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Stack,
  Avatar,
  useTheme,
  alpha,
} from '@mui/material'
import {
  TrendingUp,
  TrendingDown,
  MoreVert,
  Group as GroupIcon,
  Person,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { Transaction } from '../../types'

interface TransactionCardProps {
  transaction: Transaction
  onMenuClick: (event: React.MouseEvent<HTMLElement>, transaction: Transaction) => void
}

const TransactionCard: React.FC<TransactionCardProps> = ({ transaction, onMenuClick }) => {
  const theme = useTheme()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount)
  }

  const formatSafeDate = (dateValue: string | null | undefined): string => {
    try {
      if (!dateValue || dateValue === null || dateValue === undefined) {
        return 'No Date'
      }
      
      const date = new Date(dateValue)
      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }
      return format(date, 'MMM dd, yyyy')
    } catch (error) {
      return 'Invalid Date'
    }
  }

  const getTypeColor = (type: string) => {
    return type === 'income' ? theme.palette.success.main : theme.palette.error.main
  }

  const getTypeIcon = (type: string) => {
    return type === 'income' ? <TrendingUp /> : <TrendingDown />
  }

  const getTypeBackgroundColor = (type: string) => {
    return type === 'income' 
      ? alpha(theme.palette.success.main, 0.1)
      : alpha(theme.palette.error.main, 0.1)
  }

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header Row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            {/* Type Icon */}
            <Avatar
              sx={{
                width: 40,
                height: 40,
                backgroundColor: getTypeBackgroundColor(transaction.type),
                color: getTypeColor(transaction.type),
              }}
            >
              {getTypeIcon(transaction.type)}
            </Avatar>
            
            {/* Description and Date */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {transaction.description}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                {formatSafeDate(transaction.transactionDate)}
                {transaction.group && (
                  <>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: 'currentColor' }} />
                    <GroupIcon sx={{ fontSize: 12 }} />
                    {transaction.group.name}
                  </>
                )}
                {!transaction.group && (
                  <>
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: 'currentColor' }} />
                    <Person sx={{ fontSize: 12 }} />
                    Personal
                  </>
                )}
              </Typography>
            </Box>
          </Box>

          {/* Amount and Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: getTypeColor(transaction.type),
                textAlign: 'right',
              }}
            >
              {transaction.type === 'income' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => onMenuClick(e, transaction)}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.1),
                },
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Category and Tags Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          {/* Category */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            <Chip
              label={transaction.category?.name || 'Uncategorized'}
              size="small"
              sx={{
                backgroundColor: transaction.category?.color || theme.palette.grey[400],
                color: 'white',
                fontWeight: 500,
                fontSize: '0.75rem',
                height: 24,
              }}
            />
          </Box>

          {/* Tags */}
          {transaction.tags && transaction.tags.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {transaction.tags.slice(0, 2).map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: tag.color || theme.palette.grey[400],
                    color: tag.color || theme.palette.text.secondary,
                    fontSize: '0.7rem',
                    height: 20,
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              ))}
              {transaction.tags.length > 2 && (
                <Chip
                  label={`+${transaction.tags.length - 2}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: theme.palette.grey[400],
                    color: theme.palette.text.secondary,
                    fontSize: '0.7rem',
                    height: 20,
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              )}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default TransactionCard